/**
 * Shopify Forms - required-field patch for address block sub-fields
 * ---------------------------------------------------------------
 * Why this exists:
 * In the Shopify Forms app bundle (forms extension), the address block only
 * passes merchant validators through to three sub-fields:
 *
 *     Jie = ["country", "address1", "city"]
 *     validators: Jie.includes(fieldName) ? validators : []
 *
 * Every other sub-field (province/zone, zip, phone, address2, company,
 * first_name, last_name) is handed an empty validator array, so the
 * "required" toggle in the admin never reaches the form runtime.
 *
 * The province selector has a second, independent defect: the country
 * selector is rendered with `rules: validators`, the province selector is
 * rendered with no `rules` prop at all.
 *
 * The form element is rendered with noValidate, so adding a native
 * `required` attribute does nothing. Validation is handled by react-hook-form
 * inside an open shadow root on <shopify-forms-embed>.
 *
 * What this does (v2):
 * The app can start its submission from a delegated *click* handler on the
 * submit button (react-hook-form's handleSubmit invoked directly), not only
 * from the form's submit event - cancelling the submit event alone shows the
 * error but lets that click-driven path finish a moment later. This version
 * intercepts in the CAPTURE phase on the shadow root itself, which runs
 * before every listener React attaches inside the embed, and gates all three
 * ways a submission can start: submit-button click, the form submit event,
 * and Enter in a text input. When the gated fields are empty the event is
 * cancelled with preventDefault() + stopImmediatePropagation(), so the app's
 * submit logic never runs at all.
 *
 * Install:
 * Upload as an asset and include it near the end of theme.liquid:
 *   <script src="{{ 'shopify-forms-required-patch.js' | asset_url }}" defer></script>
 * It is form-agnostic and will apply to every Shopify Forms embed on the page.
 *
 * Remove this file once Shopify ships a fix.
 */
(function () {
  'use strict';

  var CONFIG = {
    // Field name suffixes the app generates for address sub-fields.
    zoneSuffix: '_zone',
    phoneSuffix: '_phone',

    // The phone input pre-fills the country dial code, so it is never truly
    // empty. Dial codes are 1 to 3 digits, so anything shorter than this is
    // treated as blank. Raise it if you want to enforce a fuller number.
    minPhoneDigits: 4,

    messages: {
      zone: 'Please select a state.',
      phone: 'Please enter a phone number.'
    }
  };

  var ERROR_CLASS = 'cs-forms-required-error';
  var VALUE_SUFFIX = '-selector-value';
  var seenRoots = new WeakSet();

  function buildError(message) {
    var node = document.createElement('p');
    node.className = ERROR_CLASS;
    node.textContent = message;
    node.setAttribute('aria-live', 'polite');
    node.style.color = 'var(--form-error-color, #e02229)';
    node.style.font = 'var(--forms-body-font, inherit)';
    node.style.lineHeight = '1.3';
    node.style.margin = '4px 0 0';
    node.style.textAlign = 'left';
    return node;
  }

  function clearErrors(scope) {
    var nodes = scope.querySelectorAll('.' + ERROR_CLASS);
    Array.prototype.forEach.call(nodes, function (node) {
      node.parentNode.removeChild(node);
    });
  }

  function showError(anchor, message) {
    if (!anchor || !anchor.parentNode) return;
    if (anchor.parentNode.querySelector('.' + ERROR_CLASS)) return;
    anchor.parentNode.insertBefore(buildError(message), anchor.nextSibling);
  }

  function endsWith(value, suffix) {
    return value.length >= suffix.length && value.slice(-suffix.length) === suffix;
  }

  // The state dropdown is a button, not a select. When nothing is chosen the
  // app renders a placeholder <span> inside the value span. When a state is
  // chosen it renders the label as plain text.
  function collectZoneFields(shadow) {
    var results = [];
    var spans = shadow.querySelectorAll('span[id$="' + VALUE_SUFFIX + '"]');

    Array.prototype.forEach.call(spans, function (span) {
      var name = (span.id || '').slice(0, -VALUE_SUFFIX.length);
      if (!endsWith(name, CONFIG.zoneSuffix)) return;

      var button = span.closest('button');
      var isEmpty = span.firstElementChild !== null || span.textContent.trim() === '';

      results.push({
        empty: isEmpty,
        focusTarget: button,
        anchor: button ? button.parentElement : span.parentElement,
        message: CONFIG.messages.zone
      });
    });

    return results;
  }

  function collectPhoneFields(shadow) {
    var results = [];
    var inputs = shadow.querySelectorAll('input[name]');

    Array.prototype.forEach.call(inputs, function (input) {
      if (!endsWith(input.getAttribute('name') || '', CONFIG.phoneSuffix)) return;

      var digits = (input.value || '').replace(/\D/g, '');

      results.push({
        empty: digits.length < CONFIG.minPhoneDigits,
        focusTarget: input,
        anchor: input,
        message: CONFIG.messages.phone
      });
    });

    return results;
  }

  function invalidFields(shadow) {
    if (!shadow.querySelector('form[data-testid="form"]')) return [];
    return collectZoneFields(shadow)
      .concat(collectPhoneFields(shadow))
      .filter(function (field) { return field.empty; });
  }

  // The country and state selectors are also <button>s inside the form -
  // those must stay clickable while errors are showing, or the visitor could
  // never fix the state error. Only treat a button as the submit control if
  // it is not a dropdown trigger.
  function isSubmitButton(button) {
    if (!button) return false;
    if (button.hasAttribute('aria-haspopup') || button.hasAttribute('aria-expanded')) return false;
    if (button.querySelector('[id$="' + VALUE_SUFFIX + '"]')) return false;
    return (button.getAttribute('type') || 'submit').toLowerCase() === 'submit';
  }

  function blockAndReport(event, shadow, invalid) {
    // Capture phase on the shadow root: stopImmediatePropagation() here means
    // no listener inside the embed (React's included) ever sees this event.
    event.preventDefault();
    event.stopImmediatePropagation();

    clearErrors(shadow);
    invalid.forEach(function (field) { showError(field.anchor, field.message); });
    if (invalid[0].focusTarget) invalid[0].focusTarget.focus();
  }

  function attach(shadow) {
    if (seenRoots.has(shadow)) return;
    seenRoots.add(shadow);

    // Listeners live on the shadow root, not the form, so they survive React
    // remounting the form element and need no inner MutationObserver.

    shadow.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var button = target.closest('button');
      if (button && button.closest('form[data-testid="form"]') && isSubmitButton(button)) {
        var invalid = invalidFields(shadow);
        if (invalid.length) blockAndReport(event, shadow, invalid);
        return;
      }

      if (target.closest('[role="option"]')) clearErrors(shadow);
    }, true);

    shadow.addEventListener('submit', function (event) {
      var invalid = invalidFields(shadow);
      if (invalid.length) blockAndReport(event, shadow, invalid);
    }, true);

    shadow.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      var target = event.target;
      if (!target || target.tagName !== 'INPUT') return;
      if (!target.closest || !target.closest('form[data-testid="form"]')) return;

      var invalid = invalidFields(shadow);
      if (invalid.length) blockAndReport(event, shadow, invalid);
    }, true);

    shadow.addEventListener('input', function () { clearErrors(shadow); }, true);
  }

  function sweep() {
    var embeds = document.querySelectorAll('shopify-forms-embed');

    Array.prototype.forEach.call(embeds, function (embed) {
      if (embed.shadowRoot) attach(embed.shadowRoot);
    });
  }

  new MutationObserver(sweep).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  }

  sweep();
})();
